import React, { createContext, useContext, useState, useCallback } from "react";
import { getApartments, getApartmentsByPlace } from "../services/apartmentService";
import { getBookings, getBookingsByPlace, deleteBooking as deleteBookingService, getBookingsByUserIdAndPlaceId } from "../services/bookingService";
import { getConsumptionReport } from "../services/consumptionService";
import { getVehicles, getVehiclesByUserAndPlace, getVehiclesByPlateLike } from "../services/vehicleService";
import { getChargers } from "../services/chargerService";
import { getParkers } from "../services/parkerService";

const MovilidadElectricaContext = createContext();

export const MovilidadElectricaProvider = ({ children }) => {
  const [apartments, setApartments] = useState({ data: [], total: 0 });
  const [bookings, setBookings] = useState({ data: [], total: 0 });
  const [consumptionData, setConsumptionData] = useState({ data: [], total_consumption: 0 });
  const [vehicles, setVehicles] = useState({ data: [], total: 0 });
  const [chargers, setChargers] = useState({ data: [], page: 1, pages: 1 });
  const [parkers, setParkers] = useState({ data: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refreshApartments = useCallback(async (page = 1, perPage = 1000) => {
    setLoading(true);
    setError(null);
    const placeId = sessionStorage.getItem("selectedPlaceId");
    try {
      let data;
      if (placeId) {
        data = await getApartmentsByPlace(placeId, page, perPage);
      } else {
        data = await getApartments(page, perPage);
      }
      setApartments(data);
    } catch (error) {
      console.error("Error al cargar los apartamentos:", error);
      setError(error);
      setApartments({ data: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshBookings = useCallback(async (params) => {
    setLoading(true);
    setError(null);
    const placeId = sessionStorage.getItem("selectedPlaceId");
    const user = JSON.parse(sessionStorage.getItem("user"));
    try {
      let data;
      if (placeId && user) {
        if (user.is_admin) {
          const { user_id, ...restParams } = params || {};
          if (user_id) {
            data = await getBookingsByUserIdAndPlaceId(user_id, placeId, restParams);
          } else {
            data = await getBookingsByPlace(placeId, params);
          }
        } else {
          data = await getBookingsByUserIdAndPlaceId(user.id, placeId, params);
        }
      } else {
        data = { data: [], total: 0 };
      }
      setBookings(data || { data: [], total: 0 });
    } catch (error) {
      console.error("Error al cargar las reservas:", error);
      setError(error);
      setBookings({ data: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, setBookings]);

  const refreshConsumptionData = useCallback(async (params) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getConsumptionReport(params);
      // Estructura de respuesta confirmada: { total_consumption: number, data: [...] }
      setConsumptionData({ data: data.data || [], total_consumption: data.total_consumption || 0 });
    } catch (error) {
      console.error("Error al cargar el reporte de consumo:", error);
      setError(error);
      setConsumptionData({ data: [], total_consumption: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshVehicles = useCallback(async (params) => {
    setLoading(true);
    setError(null);
    const placeId = sessionStorage.getItem("selectedPlaceId");
    const { searchTerm, ...restParams } = params;
    try {
      let data;
      if (searchTerm) {
        data = await getVehiclesByPlateLike(searchTerm, restParams);
      } else if (placeId) {
        data = await getVehiclesByUserAndPlace({ ...restParams, place_id: placeId });
      } else {
        data = await getVehicles(restParams);
      }
      setVehicles(data);
    } catch (error) {
      console.error("Error al cargar los vehículos:", error);
      setError(error);
      setVehicles({ data: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshChargers = useCallback(async (params) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getChargers(params);
      if (Array.isArray(response)) {
        setChargers({ data: response, page: 1, pages: 1 });
      } else {
        setChargers({ data: response.data, page: response.page, pages: response.pages });
      }
    } catch (error) {
      console.error("Error al cargar los cargadores:", error);
      setError(error);
      setChargers({ data: [], page: 1, pages: 1 });
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshParkers = useCallback(async () => {
    setLoading(true);
    setError(null);
    const placeId = sessionStorage.getItem("selectedPlaceId");
    const params = placeId ? { place_id: placeId } : {};
    try {
      const response = await getParkers(params);
      setParkers({ data: response.data });
    } catch (error) {
      console.error("Error al cargar los parqueaderos:", error);
      setError(error);
      setParkers({ data: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteBooking = async (bookingId) => {
    setLoading(true);
    setError(null);
    try {
      await deleteBookingService(bookingId);
    } catch (error) {
      console.error("Error deleting booking:", error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MovilidadElectricaContext.Provider value={{ apartments, refreshApartments, bookings, refreshBookings, consumptionData, refreshConsumptionData, vehicles, refreshVehicles, chargers, refreshChargers, parkers, refreshParkers, loading, error, deleteBooking }}>
      {children}
    </MovilidadElectricaContext.Provider>
  );
};

export const useMovilidadElectrica = () => {
  const context = useContext(MovilidadElectricaContext);
  if (!context) {
    throw new Error("useMovilidadElectrica debe usarse dentro de un MovilidadElectricaProvider");
  }
  return context;
};

